// deps
import { useEffect, useRef, useState } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";

// helpers
import { getAvailableUpiPackagesForTip } from "@dineout-payment-success-ui/helpers/nativeBridge";

// types
import { ITipPaymentOption } from "@dineout-payment-success-ui/interfaces";

export const filterTipPaymentOptionsByAvailability = (
    options: ITipPaymentOption[],
    availablePackages: string[],
): ITipPaymentOption[] =>
    (options || []).filter(
        (option) =>
            isEmpty(option.upiAppPackageName) ||
            option.upiAppPackageName?.some((packageName) => availablePackages.includes(packageName)),
    );

interface UseAvailableTipPaymentOptionsResult {
    availablePaymentOptions: ITipPaymentOption[];
    isCheckingAvailability: boolean;
}

export const useAvailableTipPaymentOptions = (
    paymentOptions: ITipPaymentOption[] | undefined,
    isEnabled: boolean,
): UseAvailableTipPaymentOptionsResult => {
    const paymentOptionsRef = useRef(paymentOptions);
    paymentOptionsRef.current = paymentOptions;

    const [availablePaymentOptions, setAvailablePaymentOptions] = useState<ITipPaymentOption[]>([]);
    const [isCheckingAvailability, setIsCheckingAvailability] = useState<boolean>(
        isEnabled && !isEmpty(paymentOptions),
    );

    useEffect(() => {
        const currentOptions = paymentOptionsRef.current ?? [];

        if (!isEnabled || isEmpty(currentOptions)) {
            setAvailablePaymentOptions([]);
            setIsCheckingAvailability(false);
            return;
        }

        setIsCheckingAvailability(true);

        const packageNames = Array.from(new Set(currentOptions.flatMap((option) => option.upiAppPackageName ?? [])));

        getAvailableUpiPackagesForTip(packageNames)
            .then((availablePackages) => {
                setAvailablePaymentOptions(filterTipPaymentOptionsByAvailability(currentOptions, availablePackages));
            })
            .finally(() => {
                setIsCheckingAvailability(false);
            });
    }, [isEnabled]);

    return { availablePaymentOptions, isCheckingAvailability };
};
